import * as React from 'react';
import Select, { MultiValue, SingleValue, StylesConfig } from 'react-select';
import type { BaseComponentProps } from '../../types/common';

export interface SelectOption<T = any> {
  value: T;
  label: string;
}

interface SearchableMultiSelectProps<T = any> extends BaseComponentProps {
  options: SelectOption<T>[];
  value?: T[] | T;
  onChange: (selectedValues: T[] | T | null) => void;
  placeholder?: string;
  isMulti?: boolean;
  isDisabled?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  loadingMessage?: string;
  noOptionsMessage?: string;
  customStyles?: StylesConfig<SelectOption<T>, boolean>;
  classNamePrefix?: string;
  menuPortalTarget?: HTMLElement | null;
  maxMenuHeight?: number;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  error?: string | null;
  label?: string;
  required?: boolean;
  helpText?: string;
}

/**
 * SearchableMultiSelect - A reusable searchable dropdown component
 * 
 * Features:
 * - Single or multi-select modes
 * - Searchable with keyboard navigation
 * - Customizable styling
 * - Loading states
 * - Error handling
 * - Accessibility support
 * - Bootstrap integration
 * 
 * @example
 * // Multi-select usage
 * <SearchableMultiSelect
 *   options={jobTypeOptions}
 *   value={selectedJobTypes}
 *   onChange={setSelectedJobTypes}
 *   placeholder="Search for job types..."
 *   isMulti={true}
 *   label="Job Types"
 * />
 * 
 * // Single select usage
 * <SearchableMultiSelect
 *   options={stateOptions}
 *   value={selectedState}
 *   onChange={setSelectedState}
 *   placeholder="Select a state..."
 *   isMulti={false}
 *   label="State"
 * />
 */
export const SearchableMultiSelect = <T,>({
  options,
  value,
  onChange,
  placeholder = "Search and select...",
  isMulti = true,
  isDisabled = false,
  isSearchable = true,
  isClearable = true,
  isLoading = false,
  loadingMessage = "Loading...",
  noOptionsMessage = "No options available",
  customStyles,
  classNamePrefix = "react-select",
  menuPortalTarget,
  maxMenuHeight = 300,
  menuPlacement = "auto",
  closeMenuOnSelect = !isMulti,
  hideSelectedOptions = false,
  error,
  label,
  required = false,
  helpText,
  className = '',
  id,
  'data-testid': testId,
  ...props
}: SearchableMultiSelectProps<T>) => {
  // Convert values to SelectOption format
  const getSelectedValue = React.useMemo(() => {
    if (!value) return isMulti ? [] : null;

    if (isMulti) {
      const valueArray = Array.isArray(value) ? value : [value];
      return options.filter(option => valueArray.includes(option.value));
    } else {
      return options.find(option => option.value === value) || null;
    }
  }, [value, options, isMulti]);

  // Handle selection changes
  const handleChange = (
    selectedOption: MultiValue<SelectOption<T>> | SingleValue<SelectOption<T>>
  ) => {
    if (isMulti) {
      const multiSelected = selectedOption as MultiValue<SelectOption<T>>;
      const values = multiSelected ? multiSelected.map(option => option.value) : [];
      onChange(values as T[] | T);
    } else {
      const singleSelected = selectedOption as SingleValue<SelectOption<T>>;
      onChange(singleSelected ? singleSelected.value : null);
    }
  };

  // Default styles that match the application theme
  const defaultStyles: StylesConfig<SelectOption<T>, boolean> = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      border: error ? '2px solid #dc3545' : '1px solid #ced4da',
      borderRadius: '0.375rem',
      boxShadow: error
        ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
        : state.isFocused
        ? '0 0 0 0.2rem rgba(13, 110, 253, 0.25)'
        : provided.boxShadow,
      borderColor: error
        ? '#dc3545'
        : state.isFocused
        ? '#86b7fe'
        : provided.borderColor,
      '&:hover': {
        borderColor: error ? '#dc3545' : '#86b7fe',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 8px',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '36px',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#176fc9',
      borderRadius: '0.25rem',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'white',
      padding: '2px 6px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'white',
      ':hover': {
        backgroundColor: '#0d5aa7',
        color: 'white',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#176fc9' :
        state.isFocused ? '#e7f3ff' : 'white',
      color: state.isSelected ? 'white' : '#212529',
      ':hover': {
        backgroundColor: state.isSelected ? '#176fc9' : '#e7f3ff',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1050, // Ensure dropdown appears above other elements
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1050,
    }),
  };

  // Merge custom styles with defaults
  const mergedStyles = customStyles ? { ...defaultStyles, ...customStyles } : defaultStyles;

  const selectId = id || `searchable-select-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`searchable-multi-select ${className}`.trim()} {...props}>
      {label && (
        <label htmlFor={selectId} className="form-label">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}

      <Select<SelectOption<T>, boolean>
        id={selectId}
        options={options}
        value={getSelectedValue}
        onChange={handleChange}
        placeholder={placeholder}
        isMulti={isMulti}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        isClearable={isClearable}
        isLoading={isLoading}
        loadingMessage={() => loadingMessage}
        noOptionsMessage={() => noOptionsMessage}
        styles={mergedStyles}
        classNamePrefix={classNamePrefix}
        menuPortalTarget={menuPortalTarget}
        maxMenuHeight={maxMenuHeight}
        menuPlacement={menuPlacement}
        closeMenuOnSelect={closeMenuOnSelect}
        hideSelectedOptions={hideSelectedOptions}
        data-testid={testId}
      />

      {error && (
        <div className="invalid-feedback d-block searchable-multiselect-error" style={{ color: '#dc3545', fontWeight: 500 }}>
          {error}
        </div>
      )}

      {helpText && !error && (
        <div className="form-text">
          {helpText}
        </div>
      )}
    </div>
  );
};

export default SearchableMultiSelect;
